import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { AlertCircle, Loader2 } from "lucide-react";

interface BulkLabelActionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedMcqIds: string[];
  mode: 'add' | 'remove';
  onComplete: () => void;
}

export function BulkLabelActionsDialog({
  open,
  onOpenChange,
  selectedMcqIds,
  mode,
  onComplete,
}: BulkLabelActionsDialogProps) {
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const queryClient = useQueryClient();

  // Fetch exam labels
  const { data: examLabels = [] } = useQuery({
    queryKey: ['exam-labels'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('exam_labels')
        .select('id, name, slug, color')
        .order('name');
      if (error) throw error;
      return data;
    }
  });

  const handleApply = async () => {
    if (selectedLabelIds.length === 0) {
      toast({
        title: "No labels selected",
        description: "Please select at least one label",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < selectedLabelIds.length; i++) {
      const labelId = selectedLabelIds[i];
      try {
        const { data, error } = await supabase.functions.invoke('bulk-label-mcqs', {
          body: {
            labelId,
            operation: mode === 'add' ? 'apply' : 'remove',
            mcqIds: selectedMcqIds,
          }
        });
        
        if (error) throw error;
        successCount++;
      } catch (error) {
        console.error('Error processing label:', error);
        errorCount++;
      }
      
      setProgress(((i + 1) / selectedLabelIds.length) * 100);
    }

    setIsProcessing(false);
    
    if (successCount > 0) {
      queryClient.invalidateQueries({ queryKey: ['allMcqs'] });
      toast({
        title: `Labels ${mode === 'add' ? 'added' : 'removed'} successfully`,
        description: `${successCount} label(s) processed on ${selectedMcqIds.length} MCQs`,
      });
      setSelectedLabelIds([]);
      onComplete();
    }
    
    if (errorCount > 0) {
      toast({
        title: "Some operations failed",
        description: `${errorCount} label(s) failed to process`,
        variant: "destructive",
      });
    }
  };

  const toggleLabel = (labelId: string) => {
    setSelectedLabelIds(prev =>
      prev.includes(labelId)
        ? prev.filter(id => id !== labelId)
        : [...prev, labelId]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {mode === 'add' ? 'Add Labels' : 'Remove Labels'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'add'
              ? `Add exam labels to ${selectedMcqIds.length} selected MCQ${selectedMcqIds.length !== 1 ? 's' : ''}`
              : `Remove exam labels from ${selectedMcqIds.length} selected MCQ${selectedMcqIds.length !== 1 ? 's' : ''}`
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>{selectedMcqIds.length} MCQs selected</strong>
              {mode === 'add' && ' - Labels will be added to all selected MCQs'}
              {mode === 'remove' && ' - Labels will be removed from all selected MCQs'}
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label>Select Labels</Label>
            <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto p-2 border rounded-lg">
              {examLabels.map((label) => (
                <div
                  key={label.id}
                  className="flex items-center space-x-2 p-2 rounded hover:bg-accent cursor-pointer"
                  onClick={() => toggleLabel(label.id)}
                >
                  <Checkbox
                    checked={selectedLabelIds.includes(label.id)}
                    onCheckedChange={() => toggleLabel(label.id)}
                  />
                  <Badge
                    variant="outline"
                    style={{
                      borderColor: label.color,
                      color: label.color
                    }}
                  >
                    {label.name}
                  </Badge>
                </div>
              ))}
            </div>
          </div>

          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-muted-foreground text-center">
                Processing {Math.round(progress)}%...
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={selectedLabelIds.length === 0 || isProcessing}
          >
            {isProcessing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {mode === 'add' ? 'Add Labels' : 'Remove Labels'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
